// Pure, framework-agnostic formatting helpers shared by the order list views
// (OrdersList.vue, OnlineOrders.vue, TableOrders.vue). Extracted in Phase 5 to
// remove the near-identical copies that previously lived in each view. These
// functions have no Vue/Vuex dependency so they are unit-testable in isolation.

/**
 * Human-readable elapsed time between two dates.
 *
 * When `endDate` is provided the elapsed time is frozen to (end - created),
 * otherwise it counts up to "now". Returns an em dash for missing/invalid
 * `createdDate` so the UI never renders "NaN min".
 *
 * @param {string|number|Date} createdDate
 * @param {string|number|Date|null} [endDate]
 * @returns {string}
 */
export function getTimerValue (createdDate, endDate) {
  if (!createdDate) return "—";

  const createdDateObject = new Date(createdDate);
  if (isNaN(createdDateObject.getTime())) return "—";

  const end = endDate ? new Date(endDate) : new Date();

  let delta = Math.abs(end - createdDateObject) / 1000;

  const days = Math.floor(delta / 86400);
  delta -= days * 86400;

  const hours = Math.floor(delta / 3600) % 24;
  delta -= hours * 3600;

  const minutes = Math.floor(delta / 60) % 60;

  if (days > 0) return `${days} days, ${hours} hours, ${minutes} min`;
  if (hours > 0) return `${hours} hours, ${minutes} min`;
  return `${minutes} min`;
}

/**
 * Formats an online-order "serve at" timestamp as e.g. "Monday - 6/11 - 5:30 PM".
 * Returns "Now" when the value is missing or unparseable.
 *
 * @param {string|number|Date} serveAt
 * @returns {string}
 */
export function formatServeAt (serveAt) {
  if (!serveAt) return "Now";
  const date = new Date(Date.parse(serveAt));
  if (isNaN(date.getTime())) return "Now";
  const weekday = date.toLocaleDateString([], { weekday: "long" });
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${weekday} - ${date.getMonth() + 1}/${date.getDate()} - ${time}`;
}
